import { useState, type FormEvent, type ReactNode } from 'react';
import { Save } from 'lucide-react';
import { CREDENTIAL_KINDS, CREDENTIAL_KIND_NAMES, CREDENTIAL_LIMITS, NODE_CREDENTIAL_KIND_NAMES, type CredentialKind, type SignInFormSpec } from '@cp/shared';
import { Button, Checkbox, Dialog, Field, Input, PasswordInput, Select } from '@/components/ui';
import { useCreateCredential, useUpdateCredential } from '@/hooks/useCredentials';
import { toast } from '@/store/toastStore';
import { errorField, errorText } from '@/utils/errors';
import type { CredentialOwner, SiteCredentialView } from '@/types/api';

const KIND_OPTIONS = CREDENTIAL_KIND_NAMES.map((kind) => ({ value: kind, label: CREDENTIAL_KINDS[kind] }));
/** A server's: the ways a person gets into a machine (ADR-0058). */
const NODE_KIND_OPTIONS = NODE_CREDENTIAL_KIND_NAMES.map((kind) => ({ value: kind as CredentialKind, label: CREDENTIAL_KINDS[kind] }));

const FIELD_ERRORS: Record<string, string> = {
  label: 'Назовите доступ',
  url: 'Нужен адрес http:// или https://',
  signInForm: 'Адрес формы — на домене сайта или доступа; имена полей латиницей и не совпадают',
};

/** `name=value` a line, as the fixed fields of a sign-in form are typed; null when a line is not one. */
function fixedFields(text: string): Record<string, string> | null {
  const fields: Record<string, string> = {};
  for (const line of text.split('\n').map((item) => item.trim()).filter(Boolean)) {
    const at = line.indexOf('=');
    if (at <= 0) return null;
    fields[line.slice(0, at).trim()] = line.slice(at + 1).trim();
  }
  return fields;
}

/**
 * Adding or changing a credential. When changing one, the password field
 * starts empty and an empty field leaves the stored password as it is: the
 * form never shows it and never wipes it by accident.
 *
 * A site's CMS login may also say how its CMS's sign-in form looks
 * (ADR-0042), so the panel signs in with it at a click on any CMS, not only
 * WordPress. A server's credential has the kinds a machine has and no form:
 * the panel never signs in with it (ADR-0058).
 */
export function CredentialDialog({
  owner,
  credential,
  onClose,
}: {
  readonly owner: CredentialOwner;
  /** Null to add a new one. */
  readonly credential: SiteCredentialView | null;
  readonly onClose: () => void;
}): ReactNode {
  const node = owner.kind === 'node';
  const [kind, setKind] = useState<CredentialKind>((credential?.kind as CredentialKind | undefined) ?? (node ? 'ssh' : 'cms'));
  const [label, setLabel] = useState(credential?.label ?? '');
  const [url, setUrl] = useState(credential?.url ?? '');
  const [login, setLogin] = useState(credential?.login ?? '');
  const [password, setPassword] = useState('');
  const [dropPassword, setDropPassword] = useState(false);
  const [notes, setNotes] = useState(credential?.notes ?? '');
  const form = credential?.signInForm ?? null;
  const [ownForm, setOwnForm] = useState(form !== null);
  const [formAction, setFormAction] = useState(form?.action ?? '');
  const [userField, setUserField] = useState(form?.userField ?? '');
  const [passwordField, setPasswordField] = useState(form?.passwordField ?? '');
  const [extra, setExtra] = useState(form ? Object.entries(form.extra).map(([name, value]) => `${name}=${value}`).join('\n') : '');

  const done = (message: string) => () => {
    toast.success(message);
    onClose();
  };
  const create = useCreateCredential(owner, { onSuccess: done('Доступ добавлен') });
  const update = useUpdateCredential(owner, { onSuccess: done('Доступ сохранён') });
  const saving = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const field = error ? errorField(error) : null;

  function submit(event: FormEvent): void {
    event.preventDefault();
    const fixed = fixedFields(extra) ?? {};
    const signInForm: SignInFormSpec | null =
      kind === 'cms' && ownForm ? { action: formAction.trim(), userField: userField.trim(), passwordField: passwordField.trim(), extra: fixed } : null;
    const fields = { kind, label: label.trim(), url: url.trim(), login: login.trim(), notes: notes.trim(), ...(node ? {} : { signInForm }) };
    if (!credential) {
      create.mutate({ ...fields, password });
      return;
    }
    update.mutate({
      id: credential.id,
      input: { ...fields, ...(dropPassword ? { password: null } : password ? { password } : {}) },
    });
  }

  return (
    <Dialog
      open
      title={credential ? 'Изменить доступ' : 'Новый доступ'}
      onClose={() => (saving ? undefined : onClose())}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button
            variant="primary"
            icon={Save}
            busy={saving}
            type="submit"
            form="site-credential"
            disabled={!label.trim() || (kind === 'cms' && ownForm && (!formAction.trim() || !userField.trim() || !passwordField.trim() || fixedFields(extra) === null))}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <form id="site-credential" className="stack" onSubmit={submit} noValidate>
        <Field label="Вид" htmlFor="credential-kind">
          <Select id="credential-kind" label="Вид" value={kind} onChange={setKind} options={node ? NODE_KIND_OPTIONS : KIND_OPTIONS} />
        </Field>
        <Field label="Название" htmlFor="credential-label" {...(field === 'label' ? { error: FIELD_ERRORS['label'] } : {})}>
          <Input
            id="credential-label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder={node ? 'root' : 'Админка ConvertStudio'}
            maxLength={CREDENTIAL_LIMITS.label}
            invalid={field === 'label'}
            autoComplete="off"
          />
        </Field>
        <Field label="Адрес" htmlFor="credential-url" hint="Необязательно" {...(field === 'url' ? { error: FIELD_ERRORS['url'] } : {})}>
          <Input
            id="credential-url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://…"
            maxLength={CREDENTIAL_LIMITS.url}
            invalid={field === 'url'}
            autoComplete="off"
            inputMode="url"
          />
        </Field>
        <Field label="Логин" htmlFor="credential-login">
          <Input id="credential-login" value={login} onChange={(event) => setLogin(event.target.value)} maxLength={CREDENTIAL_LIMITS.login} autoComplete="off" />
        </Field>
        <Field
          label="Пароль"
          htmlFor="credential-password"
          hint={credential?.hasPassword ? 'Пусто — оставить сохранённый пароль' : 'Хранится в зашифрованном виде'}
        >
          <PasswordInput
            id="credential-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            maxLength={CREDENTIAL_LIMITS.password}
            disabled={dropPassword}
            autoComplete="new-password"
          />
        </Field>
        {credential?.hasPassword ? (
          <Checkbox checked={dropPassword} onChange={setDropPassword} label="Удалить сохранённый пароль" />
        ) : null}
        {kind === 'cms' && !node ? (
          <fieldset className="field fieldset">
            <legend className="field-label">Вход из панели</legend>
            <div className="stack">
              <Checkbox
                checked={ownForm}
                onChange={setOwnForm}
                label="Входить по форме этой CMS"
                hint="Для WordPress не требуется. Для другой CMS: адрес из action формы и имена полей (name)"
              />
              {ownForm ? (
                <>
                  <Field label="Адрес формы" htmlFor="credential-form-action" {...(field === 'signInForm' ? { error: FIELD_ERRORS['signInForm'] } : {})}>
                    <Input
                      id="credential-form-action"
                      className="mono"
                      value={formAction}
                      onChange={(event) => setFormAction(event.target.value)}
                      placeholder="https://example.com/administrator/index.php"
                      inputMode="url"
                      autoComplete="off"
                    />
                  </Field>
                  <div className="form-grid">
                    <Field label="Поле логина" htmlFor="credential-form-user">
                      <Input id="credential-form-user" className="mono" value={userField} onChange={(event) => setUserField(event.target.value)} placeholder="username" autoComplete="off" spellCheck={false} />
                    </Field>
                    <Field label="Поле пароля" htmlFor="credential-form-password">
                      <Input id="credential-form-password" className="mono" value={passwordField} onChange={(event) => setPasswordField(event.target.value)} placeholder="password" autoComplete="off" spellCheck={false} />
                    </Field>
                  </div>
                  <Field
                    label="Постоянные поля"
                    htmlFor="credential-form-extra"
                    {...(fixedFields(extra) === null ? { error: 'По одному на строку: имя=значение' } : { hint: 'Необязательно. По одному на строку: имя=значение, например task=login' })}
                  >
                    <textarea id="credential-form-extra" className="input mono" rows={2} value={extra} onChange={(event) => setExtra(event.target.value)} spellCheck={false} />
                  </Field>
                  <p className="field-hint">
                    Формы с одноразовым токеном не поддерживаются — используйте «Войти (пароль в буфере)».
                  </p>
                </>
              ) : null}
            </div>
          </fieldset>
        ) : null}
        <Field label="Заметка" htmlFor="credential-notes" hint="Не шифруется">
          <textarea
            id="credential-notes"
            className="input"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            maxLength={CREDENTIAL_LIMITS.notes}
          />
        </Field>
        {error && !field ? (
          <p className="field-error" role="alert">
            {errorText(error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
